import {
  getPackagingMinimisationSectionConfigs,
  PACKAGING_MINIMISATION_SECTION_IDS,
} from './packagingMinimisationAssessmentSections'
import { manualInstructionWithGuidance } from './complianceDocumentSentinelParsing'
import type {
  ComplianceDocumentSectionKind,
  ComplianceDocumentSectionRecord,
} from './complianceDocumentSectionRecord'
import { stripRedundantSectionHeadingFromBody } from './stripRedundantSectionHeadingFromBody'

export type PackagingMinimisationAssessmentSectionKind =
  ComplianceDocumentSectionKind

export type PackagingMinimisationAssessmentSectionRecord =
  ComplianceDocumentSectionRecord

const CANONICAL_SECTION_ID_SET = new Set<string>(
  PACKAGING_MINIMISATION_SECTION_IDS
)

export function isCanonicalPackagingMinimisationAssessmentSectionId(
  sectionId: string
): boolean {
  return CANONICAL_SECTION_ID_SET.has(sectionId)
}

export function isCustomPackagingMinimisationAssessmentAppendixSectionId(
  sectionId: string
): boolean {
  return /^appendix-/i.test(sectionId)
}

function inferSectionKind(
  sectionId: string
): PackagingMinimisationAssessmentSectionKind {
  return isCanonicalPackagingMinimisationAssessmentSectionId(sectionId)
    ? 'canonical'
    : 'custom'
}

export function normalizePackagingMinimisationAssessmentSectionRecord(
  section: PackagingMinimisationAssessmentSectionRecord
): PackagingMinimisationAssessmentSectionRecord {
  return {
    ...section,
    kind: inferSectionKind(section.sectionId),
    title: section.title ?? '',
    markdown: section.markdown ?? '',
  }
}

export function normalizePackagingMinimisationAssessmentSections(
  sections: PackagingMinimisationAssessmentSectionRecord[]
): PackagingMinimisationAssessmentSectionRecord[] {
  return sections.map(normalizePackagingMinimisationAssessmentSectionRecord)
}

export function parsePackagingMinimisationAssessmentStoredSections(
  value: unknown
): PackagingMinimisationAssessmentSectionRecord[] {
  if (value == null) {
    return []
  }
  if (!Array.isArray(value)) {
    return []
  }
  return normalizePackagingMinimisationAssessmentSections(value)
}

export function assertPackagingMinimisationAssessmentSectionIds(
  sections: PackagingMinimisationAssessmentSectionRecord[]
): void {
  const ids = new Set(sections.map((section) => section.sectionId))
  for (const expectedId of PACKAGING_MINIMISATION_SECTION_IDS) {
    if (!ids.has(expectedId)) {
      throw new Error(
        `Missing packaging minimisation assessment section: ${expectedId}`
      )
    }
  }
  for (const section of sections) {
    if (
      !isCanonicalPackagingMinimisationAssessmentSectionId(section.sectionId) &&
      !isCustomPackagingMinimisationAssessmentAppendixSectionId(section.sectionId)
    ) {
      throw new Error(
        `Unexpected packaging minimisation assessment section: ${section.sectionId}`
      )
    }
  }
}

export function formatPackagingMinimisationAssessmentSectionBlock(
  section: PackagingMinimisationAssessmentSectionRecord
): string {
  const title = section.title.trim()
  const heading =
    title.length > 0
      ? `## ${section.sectionId} ${title}`
      : `## ${section.sectionId}`
  const body = section.markdown.trim()
  if (body.length === 0) {
    return ''
  }
  return `${heading}\n\n${body}`
}

export function composePackagingMinimisationAssessmentMarkdown(
  preamble: string,
  sections: PackagingMinimisationAssessmentSectionRecord[]
): string {
  const sortedSections = [...sections].sort(
    (left, right) => left.sortOrder - right.sortOrder
  )
  const parts: string[] = []
  const trimmedPreamble = preamble.trimEnd()
  if (trimmedPreamble.length > 0) {
    parts.push(trimmedPreamble)
  }

  for (const section of sortedSections) {
    const block = formatPackagingMinimisationAssessmentSectionBlock(section)
    if (block.trim().length === 0) {
      continue
    }
    parts.push(block)
  }

  return parts.join('\n\n')
}

export function stripPackagingMinimisationAssessmentRedundantSectionHeadingFromBody(
  markdown: string,
  sectionId: string,
  title: string
): string {
  return stripRedundantSectionHeadingFromBody(markdown, sectionId, title)
}

export function buildInitialPackagingMinimisationAssessmentSections(): PackagingMinimisationAssessmentSectionRecord[] {
  return getPackagingMinimisationSectionConfigs().map((config, index) => {
    let markdown = config.hardcodedContent ?? ''
    if (
      markdown.trim().length === 0 &&
      config.manualCompletionInstruction != null &&
      config.manualCompletionInstruction.trim().length > 0
    ) {
      markdown = manualInstructionWithGuidance(
        config.manualCompletionInstruction
      )
    }

    return {
      sectionId: config.section,
      title: config.title,
      kind: 'canonical' as const,
      sortOrder: index,
      markdown,
    }
  })
}

export {
  resolvePackagingMinimisationAssessmentSectionPdfLayout,
} from './packagingMinimisationAssessmentSections'
